import { DEFAULT_THEME, type ClientTheme } from './themes/default-theme';

// Same-origin by default (portal is served by the same nginx that proxies
// /api to the server). Dev override via VITE_API_BASE_URL.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

interface ApiSuccess<T> {
  data: T;
}

interface ApiErrorResponse {
  error: { code: string; message: string; requestId: string };
}

async function request<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const json = await res.json();

  if (!res.ok) {
    const err = json as ApiErrorResponse;
    throw new ApiError(err.error.code, err.error.message);
  }

  return (json as ApiSuccess<T>).data;
}

/**
 * Fetch the resolved theme for a client. Never throws — on any failure
 * (network error, non-2xx, JSON parse failure) returns DEFAULT_THEME so
 * the portal always has something to render.
 */
export async function fetchClientTheme(clientId: string): Promise<ClientTheme> {
  try {
    const res = await fetch(
      `${API_BASE}/api/v1/clients/${encodeURIComponent(clientId)}/theme`,
    );
    if (!res.ok) {
      return DEFAULT_THEME;
    }
    const json = (await res.json()) as ApiSuccess<ClientTheme>;
    return json.data;
  } catch {
    return DEFAULT_THEME;
  }
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export const authApi = {
  signup: (email: string, password: string) =>
    request<{ accountId: string }>('/api/v1/accounts/signup', { email, password }),

  login: (email: string, password: string) =>
    request<{ accessToken: string; refreshToken: string }>('/api/v1/accounts/login', {
      email,
      password,
    }),

  requestPasswordReset: (email: string) =>
    request<{ message: string }>('/api/v1/accounts/password/reset/request', { email }),

  confirmPasswordReset: (token: string, newPassword: string) =>
    request<{ message: string }>('/api/v1/accounts/password/reset/confirm', {
      token,
      newPassword,
    }),

  verifyEmail: (token: string) =>
    request<{ message: string }>('/api/v1/accounts/email/verify', { token }),
};
